import { Named } from 'app/model/base/named';
import { IFullResource } from 'app/interfaces/IResourceFilesProvider';
import { Response } from 'app/services/http';
import { format, formatStringForAppType } from 'app/shared/utils/string.util';

import { INamedLoadError, LoadingStatusEnum } from 'shell-models-lib';
import { tryExtractErrorResponseFromRP } from 'app/shared/utils/http-response.util';
import { ResourceId } from 'app/shared/utils/resource-id';
import { sanitizeDirtyStingAtStrictLevel } from 'app/shared/sanitize-dom-string.pipe';

export class ResourceLoadingUtils {

    public static getResourceErrorHint(named: Named): string {
        if (named.isBad || named.isGitMismatch) {
            return ResourceLoadingUtils.getErrorHint(named.loadError, named.name);
        } else if (named.isDependentOnBadResource) {
            return stringResources.ResourceLoading_PartiallyBadResource;
        }
        return '';
    }

    public static getErrorHint(loadError: INamedLoadError, resourceName?: string): string {
        let errorHint = '';
        switch (loadError.status) {
            case LoadingStatusEnum.InvalidJson:
                errorHint = stringResources.InvalidJsonSyntaxLabel;
                break;
            case LoadingStatusEnum.NameMismatch:
                errorHint = stringResources.ResourceLoading_NameMismatch;
                break;
            case LoadingStatusEnum.TypeMismatch:
                errorHint = formatStringForAppType(stringResources.ResourceLoading_PropertyMismatch, 'type');
                break;
            case LoadingStatusEnum.SubTypeMismatch:
                errorHint = formatStringForAppType(stringResources.ResourceLoading_PropertyMismatch, 'sub-type');
                break;
            case LoadingStatusEnum.MissingSubType:
                errorHint = stringResources.ResourceLoading_MissingSubType;
                break;
            case LoadingStatusEnum.InvalidType:
                errorHint = resourceName && loadError.error ? format(
                    stringResources.ResourceLoading_SmallGenericErrorMessageWithReason,
                    sanitizeDirtyStingAtStrictLevel(resourceName),
                    sanitizeDirtyStingAtStrictLevel(loadError.error.message)
                )
                    : stringResources.ResourceLoading_InvalidType;
                break;
            case LoadingStatusEnum.RenameNotAllowed:
                errorHint = format(stringResources.JSON_Editor_NotAllowed_Template, stringResources.Rename);
                break;
            case LoadingStatusEnum.UpdateObjectIdNotAllowed:
                errorHint = format(stringResources.JSON_Editor_NotAllowed_Template, stringResources.ResourceLoading_UpdatingOid);
                break;
            case LoadingStatusEnum.NameNotUnique:
                errorHint = resourceName ?
                    format(
                        stringResources.Validator_ErrorMessage_Unique_With_Name,
                        sanitizeDirtyStingAtStrictLevel(resourceName)
                    )
                    : stringResources.Validator_ErrorMessage_Unique;
                break;
            default:
                errorHint = this.processUnkownErrorHint(loadError, resourceName);
        }
        return errorHint;
    }

    public static getResourceErrorDetails(named: Named, resource?: IFullResource): string {
        let errorMessage = '';

        if (named.isBad) {
            const loadError = named.loadError;
            switch (loadError.status) {
                case LoadingStatusEnum.InvalidJson:
                    errorMessage = format(stringResources.ResourceLoading_InvalidJson_Detailed, named.name);
                    break;
                case LoadingStatusEnum.NameMismatch:
                    errorMessage = resource ? format(stringResources.ResourceLoading_NameMismatch_Detailed, named.name, JSON.parse(resource.payload).name) :
                        stringResources.ResourceLoading_NameMismatch;
                    break;
                case LoadingStatusEnum.MissingSubType:
                    errorMessage = stringResources.ResourceLoading_MissingSubType_Detailed;
                    break;
                default:
                    errorMessage = this.processUnkownErrorHint(loadError, resource?.name ?? named.name);
                    break;
            }
        } else if (named.isDependentOnBadResource) {
            errorMessage = stringResources.ResourceLoading_PartiallyBadResource;
        }

        return errorMessage;
    }

    private static processUnkownErrorHint(loadError: INamedLoadError, resourceName?: string): string {
        const errorObject = loadError.error;
        resourceName = sanitizeDirtyStingAtStrictLevel(resourceName);
        if (errorObject && errorObject instanceof Response) {
            const parsedResponse = tryExtractErrorResponseFromRP(errorObject);
            // When the JSON is modified, we get a 404 error when we are trying to fetch a reference that does not exist.
            // This code will be updated in the future according our logs, so we can give more specific error messages.
            if (errorObject.status === 404 && parsedResponse.target) {
                const resource = ResourceId.parseFrom(parsedResponse.target);
                const referencedResourceName = resource != null ? sanitizeDirtyStingAtStrictLevel(resource.resourceName) : '';
                return resourceName ? format(stringResources.ResourceLoading_ResourceNotFoundErrorMessageWithNames, resourceName, referencedResourceName)
                    : format(stringResources.ResourceLoading_ResourceNotFoundErrorMessage, referencedResourceName);
            }
        }

        const possibleReason = sanitizeDirtyStingAtStrictLevel(loadError.error?.message) ?? loadError.status;
        return resourceName ? format(stringResources.ResourceLoading_GenericErrorMessageWithNameAndReason, resourceName, loadError.status, possibleReason)
            : format(stringResources.ResourceLoading_GenericErrorMessageWithReason, loadError.status, possibleReason);
    }
}
